/**
 * Moteur d'estimation des primes CEE agricoles (côté client).
 *
 * Calcule une fourchette indicative à partir des travaux sélectionnés
 * à l'étape 4 et des suggestions complémentaires déduites du contexte
 * (serres / élevage / stockage / équipements actuels).
 *
 * Les chiffres sont des ordres de grandeur basés sur le marché EMMY 2026
 * — le montant définitif dépend du volume KWh cumac et du prix de cession.
 */

import {
  findOrientation,
  findTravauxAgri,
  TRAVAUX_AGRI,
  type TravauxAgriOption,
} from './agri-config';
import type { AgriFunnelState } from './useAgriStore';

export interface AgriEstimationResult {
  primeMin: number;
  primeMax: number;
  travauxSelectionnes: Array<{
    id: string;
    label: string;
    category: string;
    primeMin: number;
    primeMax: number;
  }>;
  /** Travaux recommandés non sélectionnés mais pertinents (suggestion). */
  suggestions: Array<{
    id: string;
    label: string;
    raison: string;
    primeMin: number;
    primeMax: number;
  }>;
  /** Liste unique des codes fiches CEE éligibles. */
  fichesEligibles: string[];
  warnings: string[];
}

/* ─── Helpers ────────────────────────────────────────────── */

function uniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

/**
 * Déduit les travaux recommandés à partir du contexte (serres / élevage / stockage)
 * sans demander de saisie supplémentaire.
 */
function deriveSuggestionsFromContext(form: AgriFunnelState): string[] {
  const suggestions: string[] = [];

  // Serres chauffées → écrans thermiques + régulation + double-tube
  if (form.serres.has && form.serres.chauffee) {
    suggestions.push('serre-double-ecran');
    suggestions.push('serre-regulation');
    if (form.serres.combustible === 'gaz' || form.serres.combustible === 'fuel') {
      suggestions.push('energie-chaudiere-serre');
      suggestions.push('energie-recup-condensation');
    }
    if (form.serres.combustible === 'biomasse' || form.serres.combustible === 'granules') {
      suggestions.push('energie-stockage-chaud');
    }
  }
  // Serres froides ou simples → couverture performante + parois
  if (form.serres.has && form.serres.chauffee === false) {
    suggestions.push('serre-couverture');
    suggestions.push('serre-ecrans-lateraux');
  }
  // Régulation manuelle → suggérer régulation informatisée
  if (form.serres.has && form.serres.regulationInformatisee === false) {
    suggestions.push('serre-regulation');
  }

  // Élevage bovin laitier → tank et récupération
  if (form.elevage.has && form.elevage.types.includes('bovin-lait')) {
    if (form.elevage.hasTank) {
      suggestions.push('elev-recup-tank');
      suggestions.push('elev-prerefroidisseur');
    }
    suggestions.push('elev-pac');
  }
  // Volailles → échangeur air/air
  if (form.elevage.has && form.elevage.types.includes('volaille')) {
    suggestions.push('elev-echangeur-volaille');
  }
  // Porcin → PAC + récup
  if (form.elevage.has && form.elevage.types.includes('porcin')) {
    suggestions.push('elev-pac');
  }

  // Stockage céréales → régulation ventilation silos
  if (form.stockage.has && form.stockage.usages.includes('cereales-grains')) {
    suggestions.push('stock-regulation-silo');
  }
  // Stockage avec séchage → séchage solaire
  if (form.stockage.has && form.stockage.usages.includes('foin-paille')) {
    suggestions.push('stock-sechage-solaire');
  }

  // Stockage réfrigéré → régulation froid HP/BP flottante
  if (form.stockage.has && form.stockage.refrigere) {
    suggestions.push('util-bp-flottante');
    suggestions.push('util-hp-flottante');
  }

  // Équipements détectés
  if (form.equipements.includes('pompage-irrigation')) {
    suggestions.push('util-variateur-async');
    suggestions.push('util-variateur-synchrone');
  }
  if (form.equipements.includes('variateurs-vitesse')) {
    suggestions.push('util-variateur-synchrone');
  }
  if (form.equipements.includes('sechage-grain')) {
    suggestions.push('stock-sechage-solaire');
  }
  if (form.equipements.includes('froid-commercial')) {
    suggestions.push('util-bp-flottante');
    suggestions.push('util-hp-flottante');
  }

  return uniq(suggestions);
}

function getSuggestionRaison(
  form: AgriFunnelState,
  travail: TravauxAgriOption,
): string {
  switch (travail.id) {
    case 'serre-double-ecran':
    case 'serre-regulation':
      return 'Serre chauffée détectée';
    case 'serre-couverture':
    case 'serre-ecrans-lateraux':
      return 'Optimisation enveloppe serre';
    case 'energie-chaudiere-serre':
    case 'energie-recup-condensation':
      return 'Chauffage fossile à remplacer';
    case 'energie-stockage-chaud':
      return 'Optimisation chaudière biomasse';
    case 'elev-recup-tank':
    case 'elev-prerefroidisseur':
      return 'Tank à lait présent';
    case 'elev-pac':
      return 'Bâtiment d\'élevage chauffé';
    case 'elev-echangeur-volaille':
      return 'Élevage volailles ventilé';
    case 'stock-regulation-silo':
      return 'Silo de stockage céréales';
    case 'stock-sechage-solaire':
      return 'Activité de séchage détectée';
    case 'util-bp-flottante':
    case 'util-hp-flottante':
      return 'Production de froid présente';
    case 'util-variateur-async':
    case 'util-variateur-synchrone':
      return 'Moteurs et pompage à optimiser';
    default:
      return findOrientation(form.orientation!)?.label ?? 'Recommandé pour votre profil';
  }
}

/* ─── Moteur ─────────────────────────────────────────────── */

/**
 * Somme dégressive : la somme linéaire surestime les dossiers multi-opérations
 * (plafonds par fiche, volumes kWhc mutualisés). Les 3 postes les plus
 * importants comptent à 100 %, les suivants à 60 %.
 */
function degressiveSum(values: number[]): number {
  const sorted = [...values].sort((a, b) => b - a);
  return Math.round(sorted.reduce((sum, v, i) => sum + (i < 3 ? v : v * 0.6), 0));
}

export function computeAgriEstimation(form: AgriFunnelState): AgriEstimationResult {
  const selectedTravaux = form.travaux
    .map((id) => findTravauxAgri(id))
    .filter((t): t is TravauxAgriOption => !!t);

  const primeMin = degressiveSum(selectedTravaux.map((t) => t.primeMin));
  const primeMax = degressiveSum(selectedTravaux.map((t) => t.primeMax));

  const suggestionIds = deriveSuggestionsFromContext(form);
  const suggestions = suggestionIds
    .filter((id) => !form.travaux.includes(id))
    .map((id) => findTravauxAgri(id))
    .filter((t): t is TravauxAgriOption => !!t)
    .slice(0, 6)
    .map((t) => ({
      id: t.id,
      label: t.label,
      raison: getSuggestionRaison(form, t),
      primeMin: t.primeMin,
      primeMax: t.primeMax,
    }));

  const fichesEligibles = uniq(selectedTravaux.flatMap((t) => t.ceeCodes));

  const warnings: string[] = [];
  if (selectedTravaux.length === 0 && suggestions.length === 0) {
    warnings.push(
      "Aucun poste de travaux identifié — un conseiller peut analyser votre exploitation et identifier des opportunités CEE.",
    );
  }
  if (form.elevage.has && form.elevage.effectif && form.elevage.effectif < 50) {
    warnings.push(
      "Sur des effectifs réduits, certaines aides PCAE ou FranceAgriMer peuvent être plus avantageuses que les CEE seules.",
    );
  }
  if (selectedTravaux.length > 3) {
    warnings.push(
      'Estimation consolidée sur plusieurs postes : le montant exact dépend des plafonds de chaque fiche CEE — il sera affiné par votre conseiller.',
    );
  }

  return {
    primeMin,
    primeMax,
    travauxSelectionnes: selectedTravaux.map((t) => ({
      id: t.id,
      label: t.label,
      category: t.category,
      primeMin: t.primeMin,
      primeMax: t.primeMax,
    })),
    suggestions,
    fichesEligibles,
    warnings,
  };
}

export function fmtEuroAgri(n: number): string {
  return n.toLocaleString('fr-FR');
}

/** Récupère la liste de tous les travaux disponibles selon le contexte. */
export function getAvailableTravaux(form: AgriFunnelState): TravauxAgriOption[] {
  // Filtrer selon les sections révélées par l'orientation
  if (!form.orientation) return TRAVAUX_AGRI;
  const orientation = findOrientation(form.orientation);
  if (!orientation) return TRAVAUX_AGRI;

  return TRAVAUX_AGRI.filter((t) => {
    if (t.category === 'serres' && !orientation.reveals.serres && !form.serres.has) return false;
    if (t.category === 'elevage' && !orientation.reveals.elevage && !form.elevage.has) return false;
    return true;
  });
}
